import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [password,     setPassword]     = useState('')
  const [confirm,      setConfirm]      = useState('')
  const [loading,      setLoading]      = useState(false)
  const [error,        setError]        = useState('')
  const [ready,        setReady]        = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    // ONLY activate if this is a password recovery link!!
    const hash   = window.location.hash
    const params = new URLSearchParams(hash.replace('#', ''))
    const type   = params.get('type')

    // If NOT a recovery link — redirect to login immediately!!
    if (type !== 'recovery') {
      window.location.replace('/login')
      return
    }

    const accessToken  = params.get('access_token')
    const refreshToken = params.get('refresh_token')

    if (accessToken) {
      supabase.auth.setSession({
        access_token:  accessToken,
        refresh_token: refreshToken || '',
      }).then(({ error }) => {
        if (!error) setReady(true)
        else window.location.replace('/login')
      })
    } else {
      window.location.replace('/login')
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (password.length < 8) return setError('Password must be at least 8 characters')
    if (password !== confirm) return setError('Passwords do not match')
    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      toast.success('Password updated! Please sign in with your new password.')
      navigate('/login')
    } catch (err) {
      setError(err.message || 'Could not update password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const styles = `
    :focus-visible { outline: 2px solid #C9A84C; outline-offset: 3px; border-radius: 4px; }
    .rp-input {
      width: 100%; padding: 12px 16px; border: 1.5px solid #E8E6DF;
      border-radius: 10px; font-size: 14px; font-family: 'DM Sans', sans-serif;
      background: #F8F7F4; color: #0D1B3E; transition: border-color 0.2s;
      box-sizing: border-box;
    }
    .rp-input:focus { border-color: #C9A84C; background: #fff; outline: none; }
    .rp-btn {
      width: 100%; padding: 14px; background: #0D1B3E; color: #C9A84C;
      border: none; border-radius: 10px; font-size: 13px; font-weight: 500;
      letter-spacing: 0.06em; cursor: pointer; transition: opacity 0.2s;
      font-family: 'DM Sans', sans-serif; margin-top: 4px;
    }
    .rp-btn:hover { opacity: 0.88; }
    .rp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .rp-error {
      background: #FDF2F2; border: 1px solid #F5C6C6;
      border-radius: 8px; padding: 10px 14px;
      font-size: 13px; color: #C0392B; margin-bottom: 14px;
    }
    .rp-label {
      display: block; font-size: 12px; font-weight: 500;
      color: #7A7870; margin-bottom: 6px; letter-spacing: 0.03em;
    }
  `

  return (
    <div style={{
      minHeight: '100vh', background: '#0D1B3E',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <style>{styles}</style>

      {/* Logo */}
      <Link to="/" style={{ textDecoration: 'none', marginBottom: 28, textAlign: 'center' }}>
        <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: '#C9A84C', fontWeight: 600 }}>
          Loan Closing Agent LLC
        </div>
        <div style={{ fontSize: 9, color: 'rgba(201,168,76,0.5)', letterSpacing: '0.14em', marginTop: 3 }}>
          NATIONWIDE SIGNINGS &amp; NOTARY GROUP
        </div>
      </Link>

      <div style={{
        background: '#fff', borderRadius: 20,
        padding: '32px 28px', width: '100%', maxWidth: 420,
      }}>
        {!ready ? (
          /* Waiting for Supabase auth event */
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>🔐</div>
            <h1 style={{ fontSize: 22, color: '#0D1B3E', fontFamily: 'Cormorant Garamond, serif', marginBottom: 8 }}>
              Verifying your link...
            </h1>
            <p style={{ fontSize: 13, color: '#7A7870', lineHeight: 1.7 }}>
              Please wait while we verify your reset link!!
            </p>
            <div style={{ marginTop: 20 }}>
              <div className="spinner" style={{ margin: 'auto' }} />
            </div>
          </div>
        ) : (
          /* Password reset form */
          <>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#FDF6E3', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 24 }}>🔑</div>
              <h1 style={{ fontSize: 22, color: '#0D1B3E', fontFamily: 'Cormorant Garamond, serif', marginBottom: 6 }}>
                Set New Password
              </h1>
              <p style={{ fontSize: 13, color: '#7A7870' }}>
                Choose a strong password — at least 8 characters!!
              </p>
            </div>

            {error && <div className="rp-error">{error}</div>}

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ marginBottom: 16 }}>
                <label className="rp-label" htmlFor="new-password">New password</label>
                <div style={{ position:'relative' }}>
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    className="rp-input"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoFocus
                    autoComplete="new-password"
                    style={{ paddingRight:44 }}
                  />
                  <button type="button" onClick={() => setShowPassword(s=>!s)} style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#7A7870', fontSize:18 }} aria-label={showPassword?'Hide':'Show'}>{showPassword?'🙈':'👁️'}</button>
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label className="rp-label" htmlFor="confirm-password">Confirm new password</label>
                <input
                  id="confirm-password"
                  type="password"
                  className="rp-input"
                  placeholder="Re-enter your password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                />
                {/* Live match indicator */}
                {confirm && (
                  <div style={{ fontSize: 11, marginTop: 5, color: password === confirm ? '#2D7A4F' : '#E24B4A' }}>
                    {password === confirm ? '✓ Passwords match' : 'Passwords do not match yet'}
                  </div>
                )}
              </div>
              <button
                type="submit"
                className="rp-btn"
                disabled={loading || password !== confirm || password.length < 8}
              >
                {loading ? 'Updating password...' : 'Update Password →'}
              </button>
            </form>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Link to="/login" style={{ fontSize: 12, color: '#9A7A30', textDecoration: 'none', fontWeight: 500 }}>
                ← Back to Sign In
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
